import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Dropdown, DropdownItem } from '../../dropdown';
import {
  FilterBarContainer,
  DateChipContainer,
  DateChip,
  LeftContent,
  RightContent,
  AssigneeDropdown,
  Icon,
  ArrowIcon,
  DateText,
  BottomData,
  Label
} from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { rootStore } from '../../../mobx/rootStore';
import { SelectRangeInterface } from '../../../interfaces/ReportInterface';
import { isEmpty } from '../../../helpers/common';
import { getWorkspaceItemPreferenceData, updateWorkspaceItemPreference } from '../../../helpers/firebaseHelper';
import { ICON_SIZE, USER_ROLE } from '../../../global/constants';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);

interface UsersListStateInterface {
  id: string;
  name: string;
  profile_photo: string;
  checked: boolean;
}

interface WorkloadFilterBarProps {
  selectionRange: SelectRangeInterface;
  onUsersChange?: (userIds: string[]) => void;
}

const WorkloadFilterBar: React.FC<WorkloadFilterBarProps> = ({ selectionRange, onUsersChange }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchUser, setSearchUser] = useState<string>('');
  const [isUserSearch, setIsUserSearch] = useState<boolean>(false);
  const [userSearchResult, setUserSearchResult] = useState<UsersListStateInterface[]>([]);
  const [selectAllUser, setSelectAllUser] = useState<boolean>(true);
  const [searchResetKey, setSearchResetKey] = useState(0);

  // MobX store
  const { workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;

  const [usersList, setUsersList] = useState<UsersListStateInterface[]>([]);

  // Initialize users list when workspace loads
  useEffect(() => {
    const init = async () => {
      if (workspace?.users) {
        const saved = (await getWorkspaceItemPreferenceData('reports', 'workload', 'workload_filter')) as
          | string[]
          | undefined;
        const initialUsers = workspace.users
          ?.filter((user) => !user.id.toLowerCase().startsWith('agent_') && user.role !== USER_ROLE.CLIENT)
          .map((user) => ({
            id: user.id,
            name: user.name,
            profile_photo: user.profile_photo || user.avatar?.avatar || '',
            checked: Array.isArray(saved) ? saved.includes(user.id) : true
          }));
        setUsersList(initialUsers);
        const allSelected = initialUsers.every((user) => user.checked);
        setSelectAllUser(allSelected);
      }
    };
    init();
  }, [workspace?.users, onUsersChange]);

  const weekDays = useMemo(() => {
    const start = dayjs(selectionRange.startDate);
    const end = dayjs(selectionRange.endDate);
    const days = [];
    let currentDate = start;

    while (currentDate.isSameOrBefore(end)) {
      days.push({
        dayName: currentDate.format('ddd'),
        date: currentDate.format('DD'),
        fullDate: currentDate.toDate()
      });
      currentDate = currentDate.add(1, 'day');
    }
    return days;
  }, [selectionRange.startDate, selectionRange.endDate]);

  const searchQueryUser = useCallback(
    (value: string) => {
      if (!isEmpty(value) && usersList?.length > 0) {
        const filteredUsers = usersList.filter((user) => user.name.toLowerCase().includes(value.toLowerCase()));
        setUserSearchResult(filteredUsers);
      } else {
        setUserSearchResult([]);
      }
    },
    [usersList]
  );

  const onChangeSearchUser = useCallback(
    (value: string) => {
      if (value) {
        setSearchUser(value);
        if (!isUserSearch) setIsUserSearch(true);
        searchQueryUser(value);
      } else {
        setSearchUser('');
        if (isUserSearch) setIsUserSearch(false);
        setUserSearchResult([]);
      }
    },
    [isUserSearch, searchQueryUser]
  );

  const onChangeUser = useCallback(
    (userId: string, checked: boolean) => {
      const updatedUsersList = usersList.map((user) => (user.id === userId ? { ...user, checked } : user));
      const isAllSelected = updatedUsersList.every((user) => user.checked);
      setSelectAllUser(isAllSelected);
      setUsersList(updatedUsersList);
      // reset search input and local search state after selection
      setSearchUser('');
      setIsUserSearch(false);
      setUserSearchResult([]);
      setSearchResetKey((k) => k + 1);
      const selectedIds = updatedUsersList.filter((user) => user.checked).map((user) => user.id);
      updateWorkspaceItemPreference('reports', 'workload', { workload_filter: selectedIds });
    },
    [usersList]
  );

  const onSelectAllUser = useCallback(
    (checked: boolean) => {
      const updatedUsersList = usersList.map((user) => ({ ...user, checked }));
      setSelectAllUser(checked);
      setUsersList(updatedUsersList);
      // reset search input and local search state after select all
      setSearchUser('');
      setIsUserSearch(false);
      setUserSearchResult([]);
      setSearchResetKey((k) => k + 1);
      setIsDropdownOpen(false);
      const selectedIds = updatedUsersList.filter((user) => user.checked).map((user) => user.id);
      updateWorkspaceItemPreference('reports', 'workload', { workload_filter: selectedIds });
    },
    [usersList]
  );

  // Calculate the display text for the assignee input
  const assigneeDisplayText = useMemo(() => {
    if (selectAllUser) {
      return 'All Members';
    }

    const selectedCount = usersList.filter((user) => user.checked).length;

    if (selectedCount === 0) {
      return 'Select Members';
    }

    return `${selectedCount} ${selectedCount === 1 ? 'member' : 'members'}`;
  }, [selectAllUser, usersList]);

  // Notify parent component when selected users change
  useEffect(() => {
    if (onUsersChange) {
      const selectedIds = usersList.filter((user) => user.checked).map((user) => user.id);
      onUsersChange(selectedIds);
    }
  }, [usersList, onUsersChange]);

  const handleUpdateDropdownOpen = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen);
  };

  //   // TODO: Implement assignee selection logic
  const renderAssigneeData = () => {
    const displayUsers = isUserSearch && !isEmpty(searchUser) ? userSearchResult : usersList;

    return (
      <BottomData>
        <DropdownItem
          label='Select All'
          isCheckBox={true}
          isChecked={selectAllUser}
          iconName={'unassign-profile-icon'}
          iconSize={20}
          iconViewBox='0 0 18 18'
          onClick={() => onSelectAllUser(!selectAllUser)}
        />
        {displayUsers?.map((user) => (
          <DropdownItem
            key={user.id}
            label={user.name}
            isCheckBox={true}
            isChecked={user.checked}
            isAvatar={true}
            avatarSrc={user.profile_photo}
            onClick={() => onChangeUser(user.id, !user.checked)}
          />
        ))}
      </BottomData>
    );
  };

  return (
    <>
      <FilterBarContainer>
        <LeftContent>
          <Dropdown
            modalTitle='Assignee'
            isMinWidth={232}
            dropdownContentWidth='100%'
            updateDropdownOpen={handleUpdateDropdownOpen}
            onChangeSearch={onChangeSearchUser}
            searchResetKey={searchResetKey}
            content={
              <AssigneeDropdown isActive={isDropdownOpen}>
                <Icon>
                  <SVGIcon
                    name='customer-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary'
                  />
                </Icon>
                {/* if selected random assignee then show like this: selected [number] member */}
                <Label isPlaceholder={assigneeDisplayText === 'Select Members'}>{assigneeDisplayText}</Label>
                <ArrowIcon isActive={isDropdownOpen}>
                  <SVGIcon
                    name='down-arrow-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary'
                  />
                </ArrowIcon>
              </AssigneeDropdown>
            }
            trigger='click'
            onOutsideClick={() => setIsDropdownOpen(false)}>
            {renderAssigneeData()}
          </Dropdown>
        </LeftContent>
        <RightContent>
          <DateChipContainer>
            {weekDays.map((day, index) => (
              <DateChip key={index}>
                <DateText>
                  {day.dayName} {day.date}
                </DateText>
              </DateChip>
            ))}
          </DateChipContainer>
        </RightContent>
      </FilterBarContainer>
    </>
  );
};

export default WorkloadFilterBar;
